import React, { useCallback, useMemo } from 'react';

import { QBox, QMenuButton, QMenuItemType } from '@qualio/ui-components';

import { QualioDocumentSection } from '../../../api/model/document';
import { EditorName } from '../../../config/handlers/EditorEventsHandlers';
import { ChangeControlSection } from '../../../types/DocumentCreate';
import {
  navigateToHeading,
  navigateToSection,
} from '../../../util/DocumentNavigationUtils';
import { DocumentPresentationUtils } from '../../../util/DocumentPresentationUtils';
import { useLiveHeadings } from '../../../hooks/useLiveHeadings';

const SUB_HEADING_INDENT = '    ';

export const TableOfContents: React.FC<{
  sections: QualioDocumentSection[] | ChangeControlSection[];
  sectionTitlesVisible: boolean;
  editorName?: EditorName;
  showSubHeadings?: boolean;
}> = ({
  sections,
  sectionTitlesVisible,
  editorName = 'documentEditorContent',
  showSubHeadings = false,
}) => {
  const liveHeadings = useLiveHeadings(editorName, showSubHeadings);

  const handleItemClick = useCallback(
    (item: QMenuItemType) => {
      if (showSubHeadings) {
        navigateToHeading(Number.parseInt(item.id), editorName);
        return;
      }
      const index = Number.parseInt(item.id) - 1;
      navigateToSection(index, editorName);
    },
    [editorName, showSubHeadings],
  );

  const TOCItems: QMenuItemType[] = useMemo(() => {
    if (!showSubHeadings) {
      return DocumentPresentationUtils.getDocumentSectionsTitles(sections).map(
        (title, idx) => ({
          id: title.split('.')[0],
          label: title,
          'data-cy': `toc-element-${idx + 1}`,
        }),
      );
    }

    let sectionCount = 0;
    return liveHeadings.map((heading, index) => {
      if (heading.isSectionTitle) {
        sectionCount++;
      }
      return {
        id: String(index),
        label: heading.isSectionTitle
          ? `${sectionCount}. ${heading.text}`
          : `${SUB_HEADING_INDENT.repeat(heading.level)}${heading.text}`,
        'data-cy': `toc-element-${index + 1}`,
      };
    });
  }, [sections, showSubHeadings, liveHeadings]);

  return (
    <QBox
      height="fit-content"
      id="documentTocControl"
      data-cy="documentTocControl"
    >
      {sectionTitlesVisible && (
        <QMenuButton
          items={TOCItems}
          onItemClick={handleItemClick}
          variant="icon"
          buttonLabel=""
          aria-label="toc-button"
          iconName="Menu"
          maxHeight="60vh"
          data-cy="toc-button"
        ></QMenuButton>
      )}
    </QBox>
  );
};
